/**
 * Exercise strip chrome: phase copy, seat vocabulary, scorebug labels.
 */
import {
  activeExerciseRun,
  formatRunLabel,
  latestExerciseRun,
  selectedBoardVersion,
} from './exerciseBoardModel'
import type { ExerciseRun, ScenarioSession } from './types'

export type ExerciseSeat = 'Leading' | 'Participating' | 'Watching'

export function exerciseSeatLabel(opts: {
  canOperate: boolean
  onTeam: boolean
}): ExerciseSeat {
  if (opts.canOperate) return 'Leading'
  if (opts.onTeam) return 'Participating'
  return 'Watching'
}

/**
 * Hub-preferring transport label inputs (lifecycle × take phase × clock).
 * Prefer these over local boardActivity when deriving strip chrome.
 */
export type RoomTransportChromeInput = {
  completed: boolean
  takeNumber: number
  takeStopped: boolean
  tapeReviewRunId: string | null | undefined
  liveTakeActive: boolean
  running: boolean
  instantReplayEngaged: boolean
  countdownActive: boolean
  versionSwitching: boolean
  editing: boolean
  /** Planned or Live Idle story preview (no tape, no live take). */
  storyPreviewing: boolean
  /** Finished-run review chrome (tape id and/or between-takes before “done reviewing”). */
  reviewing: boolean
}

/** Pure strip task from hub-ish fields — Reviewing when tape/reviewing, else Preview. */
export function deriveRoomTransportStripLabel(opts: RoomTransportChromeInput): string | null {
  if (opts.completed) return 'Reviewing'
  if (opts.versionSwitching) return 'Switching'
  if (opts.countdownActive) {
    return opts.takeNumber > 0
      ? `Starting · Run ${opts.takeNumber}`
      : 'Starting'
  }
  if (opts.editing) return 'Editing'
  if (opts.liveTakeActive) {
    if (opts.instantReplayEngaged) {
      return opts.takeNumber > 0
        ? `Instant Replay · Run ${opts.takeNumber}`
        : 'Instant Replay'
    }
    return `${opts.running ? 'Running' : 'Paused'}${
      opts.takeNumber > 0 ? ` · Run ${opts.takeNumber}` : ''
    }`
  }
  const tapeId = typeof opts.tapeReviewRunId === 'string' ? opts.tapeReviewRunId.trim() : ''
  if (opts.reviewing || tapeId) {
    return opts.takeNumber > 0 ? `Reviewing · Run ${opts.takeNumber}` : 'Reviewing'
  }
  if (opts.storyPreviewing || opts.takeStopped) {
    return opts.running ? 'Previewing' : 'Paused'
  }
  return null
}

/** Compact phase chip — Previewing|Paused / Run N (+ paused). */
export function exercisePhaseChip(opts: {
  completed: boolean
  storyPreviewing: boolean
  clockPreviewRunning: boolean
  poisedForFirstRun: boolean
  instantReplayEngaged: boolean
  liveTakeActive: boolean
  running: boolean
  betweenTakes: boolean
  liveTakePaused: boolean
  takeNumber: number
}): string | null {
  if (opts.completed) return null
  // Story preview (Live pre–Run 1 or Planned clock): Previewing while advancing,
  // Paused when stopped mid-story. At story start (elapsed 0) stay Previewing.
  if (opts.storyPreviewing || opts.clockPreviewRunning) {
    return opts.running && !opts.instantReplayEngaged ? 'Previewing' : 'Paused'
  }
  if (opts.poisedForFirstRun) return 'Previewing'
  if (opts.instantReplayEngaged || opts.liveTakePaused) {
    return opts.takeNumber > 0 ? `Run ${opts.takeNumber} paused` : 'Paused'
  }
  if (opts.liveTakeActive && opts.running && opts.takeNumber > 0) {
    return `Run ${opts.takeNumber}`
  }
  // Between takes on the live board = Previewing (not “After Run N”).
  if (opts.betweenTakes && opts.takeNumber > 0) {
    return 'Previewing'
  }
  return null
}

export function scorebugActiveRow(session: ScenarioSession): {
  runLabel: string
  versionName: string
  run: ExerciseRun | null
} {
  const active = activeExerciseRun(session)
  const latest = latestExerciseRun(session)
  const run = active ?? latest
  const version = selectedBoardVersion(session)
  const versionFromRun =
    run && session.boardVersions?.find((v) => v.id === run.versionId)?.name
  // Hot take scalars can exist before exerciseRuns[] was persisted (legacy Live boards).
  const takeN =
    typeof session.exerciseTakeNumber === 'number' && session.exerciseTakeNumber > 0
      ? Math.floor(session.exerciseTakeNumber)
      : 0
  const runLabel = run
    ? formatRunLabel(run)
    : takeN > 0
      ? `Run ${takeN}`
      : 'No runs yet'
  return {
    runLabel,
    versionName: versionFromRun ?? version?.name ?? 'Main version',
    run,
  }
}
